import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Button, Card } from '@parrot-software/design-system';
import { ChevronRight, Landmark } from '@parrot-software/design-system/icons';
import { formatCurrencyMXN } from '@subscriptions/application/utils';
import type { PaymentMethodViewDto } from '@subscriptions/application/dtos';
import { ClabeDisplay } from '@subscriptions/presentation/components/base';
import { useSubscriptionBlock } from '@subscriptions/presentation/hooks';

import type { WizardOrigin } from '@subscriptions/presentation/components/specific/BlockCreationWizard/wizardOrigin';
import {
  CREATE_ORIGIN,
  OVERDUE_PAYMENT_ORIGIN,
  REACTIVATION_ORIGIN,
  UPDATE_PAYMENT_ORIGIN,
} from '@subscriptions/presentation/components/specific/BlockCreationWizard/wizardOrigin';

import { ConfirmationSuccessHeader } from './ConfirmationSuccessHeader';
import { speiConfirmationStyles as styles } from './SpeiConfirmation.styles';

/** Title copy per entry origin: the reactivation wording is its own, not the update one. */
const SPEI_TITLE_KEYS: Record<WizardOrigin, string> = {
  [CREATE_ORIGIN]: 'blockCreationWizard.confirmation.spei.groupedTitle',
  [UPDATE_PAYMENT_ORIGIN]: 'blockCreationWizard.confirmation.spei.successTitle',
  [OVERDUE_PAYMENT_ORIGIN]: 'blockCreationWizard.confirmation.spei.successTitle',
  [REACTIVATION_ORIGIN]: 'blockCreationWizard.confirmation.spei.reactivationTitle',
};

type SpeiConfirmationProps = {
  blockUuid: string;
  origin: WizardOrigin;
  paymentMethod: PaymentMethodViewDto;
};

/**
 * Terminal SPEI confirmation (wizard step 5, `spei` variant).
 *
 * Renders the Figma two-card layout: a left "SPEI" card with the block/plan name,
 * the recurring value (`MXN`-prefixed, with the "/ mes" suffix), the "Transferencia
 * SPEI" label, an explanatory note, the hard-coded bank/recipient data
 * beside the CLABE (with a copy action via {@link ClabeDisplay}) and the closing
 * copy; and a right "Pasos a seguir" card. The columns sit side by side on desktop
 * and stack on smaller screens. The CLABE comes from the `generate-reference` (B2)
 * response carried in route state; the monthly value and plan name come from the
 * block detail. The primary action routes to the block detail page.
 *
 * The title switches on the wizard entry origin: "¡Sucursales agrupadas y SPEI
 * configurado!" in the creation flow, "¡Configuración SPEI lista!" when re-entering
 * from the block detail to change/configure the payment method.
 *
 * @param props.blockUuid - Subscription block uuid for the in-flight wizard
 * @param props.origin - Flow the wizard was entered through
 * @param props.paymentMethod - Payment method from the generate-reference response (CLABE in `providerPublicId`)
 * @returns React element rendering the SPEI confirmation
 */
export const SpeiConfirmation = ({ blockUuid, origin, paymentMethod }: SpeiConfirmationProps) => {
  const { t } = useTranslation('subscriptions');
  const history = useHistory();
  const { data: block } = useSubscriptionBlock(blockUuid);

  const monthlyValue = formatCurrencyMXN(block?.total ?? 0);

  const handleGoToSubscription = () => {
    history.push(`/subscriptions/blocks/${blockUuid}`);
  };

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        <ConfirmationSuccessHeader className={styles.header} title={t(SPEI_TITLE_KEYS[origin])} />

        <div className={styles.cards}>
          <Card className={styles.speiCard}>
            <div className={styles.speiHeader}>
              <p className={styles.planName}>{block?.name}</p>

              <div className={styles.amountRow}>
                <span className={styles.amount}>{`MXN ${monthlyValue}`}</span>
                <span className={styles.amount}>{t('blockCreationWizard.confirmation.spei.perMonthSuffix')}</span>
              </div>

              <div className={styles.methodRow}>
                <Landmark aria-hidden className="h-5 w-5 text-foreground" />
                <p className={styles.methodLabel}>{t('paymentMethod.spei')}</p>
              </div>
            </div>

            <p className={styles.note}>{t('blockCreationWizard.confirmation.spei.note')}</p>

            <div className={styles.clabeRow}>
              <div className={styles.bankBox}>
                <div className={styles.bankRow}>
                  <span className={styles.bankKey}>{t('paymentMethod.bank')}</span>
                  <span className={styles.bankValue}>{t('paymentMethod.bankName')}</span>
                </div>
                <div className={styles.bankRow}>
                  <span className={styles.bankKey}>{t('paymentMethod.recipient')}</span>
                  <span className={styles.bankValue}>{t('paymentMethod.recipientName')}</span>
                </div>
              </div>

              <ClabeDisplay className={styles.clabe} providerPublicId={paymentMethod.providerPublicId} />
            </div>

            <div className={styles.closingBox}>
              <p className={styles.closingTitle}>{t('blockCreationWizard.confirmation.spei.closingTitle')}</p>
              <p className={styles.closingDescription}>
                {t('blockCreationWizard.confirmation.spei.closingDescription')}
              </p>
            </div>
          </Card>

          <Card className={styles.stepsCard}>
            <p className={styles.stepsTitle}>{t('blockCreationWizard.confirmation.spei.stepsTitle')}</p>
            <ol className={styles.stepsList}>
              <li>{t('blockCreationWizard.confirmation.spei.step1')}</li>
              <li>{t('blockCreationWizard.confirmation.spei.step2')}</li>
              <li>{t('blockCreationWizard.confirmation.spei.step3')}</li>
              <li>{t('blockCreationWizard.confirmation.spei.step4')}</li>
              <li>{t('blockCreationWizard.confirmation.spei.step5')}</li>
            </ol>
          </Card>
        </div>

        <Button className={styles.button} onClick={handleGoToSubscription}>
          {t('blockCreationWizard.confirmation.primaryAction')}
          <ChevronRight aria-hidden className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
